/**
 * Geetest v4 CAPTCHA utility.
 * Provides slide puzzle and other interactive challenges.
 */

const DEFAULT_GEETEST_CAPTCHA_ID = 'a65b425cb243ad40df77c9dbe52ebb39'
const GEETEST_CAPTCHA_ID =
    import.meta.env.VITE_GEETEST_CAPTCHA_ID ?? DEFAULT_GEETEST_CAPTCHA_ID

declare global {
    interface Window {
        initGeetest4?: (
            config: {
                captchaId: string
                product?: 'float' | 'bind' | 'popup'
                language?: string
                protocol?: 'http://' | 'https://'
            },
            callback: (captchaObj: GeetestCaptchaObject) => void
        ) => void
    }
}

export interface GeetestCaptchaObject {
    appendTo: (selector: string | HTMLElement) => void
    getValidate: () => GeetestValidateResult | false
    reset: () => void
    destroy: () => void
    showCaptcha: () => void  // Programmatically show the challenge
    onReady: (callback: () => void) => void
    onSuccess: (callback: () => void) => void
    onError: (callback: (error: any) => void) => void
    onClose: (callback: () => void) => void
}

export interface GeetestValidateResult {
    lot_number: string
    captcha_output: string
    pass_token: string
    gen_time: string
}

let loadPromise: Promise<void> | null = null

const GEETEST_SRC = 'https://static.geetest.com/v4/gt4.js'

export const getGeetestCaptchaId = (): string => GEETEST_CAPTCHA_ID

/**
 * Load Geetest v4 script.
 */
export const loadGeetest = (): Promise<void> => {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('Geetest недоступен в SSR среде'))
    }

    if (window.initGeetest4) {
        return Promise.resolve()
    }

    if (loadPromise) {
        return loadPromise
    }

    loadPromise = new Promise<void>((resolve, reject) => {
        const existingScript = document.querySelector<HTMLScriptElement>(
            `script[src^="https://static.geetest.com"]`
        )

        if (existingScript) {
            const checkReady = () => {
                if (window.initGeetest4) {
                    resolve()
                } else {
                    setTimeout(checkReady, 50)
                }
            }
            checkReady()
            return
        }

        const script = document.createElement('script')
        script.src = GEETEST_SRC
        script.async = true
        script.defer = true
        script.onload = () => {
            const checkReady = () => {
                if (window.initGeetest4) {
                    resolve()
                } else {
                    setTimeout(checkReady, 50)
                }
            }
            checkReady()
        }
        script.onerror = (e) => {
            console.error('[GonkaGame] Failed to load Geetest script', e)
            script.remove()
            loadPromise = null
            reject(new Error('Не удалось загрузить Geetest'))
        }
        document.head.appendChild(script)
    })

    return loadPromise
}

/**
 * Initialize Geetest captcha widget.
 */
export const initGeetestWidget = async (
    containerId: string,
    callbacks: {
        onReady?: () => void
        onSuccess?: (result: GeetestValidateResult) => void
        onError?: (error: any) => void
        onClose?: () => void
    } = {}
): Promise<GeetestCaptchaObject> => {
    await loadGeetest()

    if (!window.initGeetest4) {
        throw new Error('Geetest SDK недоступен')
    }

    return new Promise((resolve) => {
        window.initGeetest4!(
            {
                captchaId: GEETEST_CAPTCHA_ID,
                product: 'bind', // 'bind' mode - no button, call showCaptcha() to trigger
                language: 'rus',
                protocol: 'https://',
            },
            (captchaObj) => {
                captchaObj.appendTo(`#${containerId}`)

                captchaObj.onReady(() => {
                    callbacks.onReady?.()
                })

                captchaObj.onSuccess(() => {
                    const result = captchaObj.getValidate()
                    if (result) {
                        callbacks.onSuccess?.(result)
                    }
                })

                captchaObj.onError((error) => {
                    console.error('[GonkaGame] Geetest error:', error)
                    callbacks.onError?.(error)
                })

                captchaObj.onClose(() => {
                    callbacks.onClose?.()
                })

                resolve(captchaObj)
            }
        )
    })
}

/**
 * Serialize Geetest result for form submission.
 * Backend expects JSON-encoded result.
 */
export const serializeGeetestResult = (result: GeetestValidateResult): string => {
    return JSON.stringify(result)
}
